import React, { useState, useEffect, useRef } from "react";
import { useCurrency } from "../context/CurrencyContext";
import "./FilterSidebar.css";

const FilterSidebar = ({
    onFilterChange,
    availableCategories = [],
    availableBrands = [],
    initialFilters = null
}) => {
    const { getCurrencySymbol, formatPriceOnly } = useCurrency();

    const [priceRange, setPriceRange] = useState(() =>
        initialFilters?.priceRange || { min: 0, max: 100000 }
    );
    const [selectedCategories, setSelectedCategories] = useState(() =>
        initialFilters?.selectedCategories || []
    );
    const [selectedBrands, setSelectedBrands] = useState(() =>
        initialFilters?.selectedBrands || []
    );
    const [expandedSections, setExpandedSections] = useState({
        price: true,
        category: true,
        brand: true
    });

    // ── Resync from initialFilters whenever it changes ──────────────────────
    // useState(() => initialFilters?.x) above only runs ONCE, on this
    // component's own first mount. If the parent restores filters
    // asynchronously (e.g. from sessionStorage, right after this component
    // has already mounted with the default values), that later prop update
    // is otherwise silently ignored — the checkboxes/slider stay unchecked
    // even though the parent is correctly using the restored filters for
    // fetching products. This effect keeps local UI state in sync whenever
    // initialFilters actually changes (content-compared, not just a new
    // object reference, to avoid update loops with the "notify parent"
    // effect below).
    const lastSyncedKeyRef = useRef(JSON.stringify({
        priceRange: initialFilters?.priceRange || { min: 0, max: 100000 },
        selectedCategories: initialFilters?.selectedCategories || [],
        selectedBrands: initialFilters?.selectedBrands || []
    }));

    useEffect(() => {
        if (!initialFilters) return;
        const incomingKey = JSON.stringify({
            priceRange: initialFilters.priceRange || { min: 0, max: 100000 },
            selectedCategories: initialFilters.selectedCategories || [],
            selectedBrands: initialFilters.selectedBrands || []
        });
        if (incomingKey === lastSyncedKeyRef.current) return;
        lastSyncedKeyRef.current = incomingKey;

        setPriceRange(initialFilters.priceRange || { min: 0, max: 100000 });
        setSelectedCategories(initialFilters.selectedCategories || []);
        setSelectedBrands(initialFilters.selectedBrands || []);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialFilters]);

    // Notify parent of filter changes
    useEffect(() => {
        onFilterChange({
            priceRange,
            selectedCategories,
            selectedBrands
        });
    }, [priceRange, selectedCategories, selectedBrands]);

    // Clear selected categories if they are no longer in availableCategories (due to brand filter change)
    useEffect(() => {
        if (selectedCategories.length > 0 && availableCategories.length > 0) {
            setSelectedCategories(prev => {
                const validSelected = prev.filter(catId =>
                    availableCategories.some(cat => cat._id === catId)
                );
                if (validSelected.length !== prev.length) {
                    return validSelected;
                }
                return prev;
            });
        }
    }, [availableCategories, selectedCategories]);

    const handlePriceChange = (e, type) => {
        const val = parseInt(e.target.value);
        if (type === 'min') {
            setPriceRange(prev => ({ ...prev, min: Math.min(val, prev.max - 1000) }));
        } else {
            setPriceRange(prev => ({ ...prev, max: Math.max(val, prev.min + 1000) }));
        }
    };

    const toggleCategory = (catId) => {
        // Toggle behavior: Allow single selection for now as per API naming convention
        setSelectedCategories(prev =>
            prev.includes(catId) ? [] : [catId]
        );
    };

    const toggleBrand = (brandId) => {
        // Toggle behavior: Allow single selection for now as per API naming convention
        setSelectedBrands(prev =>
            prev.includes(brandId) ? [] : [brandId]
        );
    };

    const toggleSection = (section) => {
        setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    const resetSection = (section) => {
        if (section === 'price') setPriceRange({ min: 0, max: 100000 });
        if (section === 'category') setSelectedCategories([]);
        if (section === 'brand') setSelectedBrands([]);
    };

    return (
        <div className="custom-filter-sidebar">
            <div className="sidebar-container-card">
                {/* Price */}
                <div className="filter-section">
                    <div className="filter-section-header" onClick={() => toggleSection('price')}>
                        <h3>Price</h3>
                        <div className="header-actions">
                            <i className={`bi bi-chevron-${expandedSections.price ? 'up' : 'down'}`}></i>
                        </div>
                    </div>
                    {expandedSections.price && (
                        <div className="filter-section-content">
                            <div className="price-slider-container">
                                <div className="slider-track"></div>
                                <input
                                    type="range"
                                    min="0"
                                    max="100000"
                                    value={priceRange.min}
                                    onChange={(e) => handlePriceChange(e, 'min')}
                                    className="price-thumb thumb-min"
                                />
                                <input
                                    type="range"
                                    min="0"
                                    max="100000"
                                    value={priceRange.max}
                                    onChange={(e) => handlePriceChange(e, 'max')}
                                    className="price-thumb thumb-max"
                                />
                            </div>
                            <div className="price-labels">
                                <span>Price: {getCurrencySymbol()} {formatPriceOnly(priceRange.min).toLocaleString()} — {getCurrencySymbol()} {formatPriceOnly(priceRange.max).toLocaleString()}</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Brand */}
                <div className="filter-section">
                    <div className="filter-section-header" onClick={() => toggleSection('brand')}>
                        <h3>Brand</h3>
                        <div className="header-actions">
                            <i className={`bi bi-chevron-${expandedSections.brand ? 'up' : 'down'}`}></i>
                        </div>
                    </div>
                    {expandedSections.brand && (
                        <div className="filter-section-content">
                            {availableBrands.length > 0 ? (
                                availableBrands.map(brand => (
                                    <div key={brand._id} className="checkbox-item">
                                        <input
                                            type="checkbox"
                                            id={`brand-${brand._id}`}
                                            checked={selectedBrands.includes(brand._id)}
                                            onChange={() => toggleBrand(brand._id)}
                                        />
                                        <label htmlFor={`brand-${brand._id}`}>{brand.name}</label>
                                    </div>
                                ))
                            ) : (
                                <p className="text-muted small">No brands available</p>
                            )}
                        </div>
                    )}
                </div>

                {/* Category (Sub-categories) */}
                <div className="filter-section">
                    <div className="filter-section-header" onClick={() => toggleSection('category')}>
                        <h3>Category</h3>
                        <div className="header-actions">
                            <i className={`bi bi-chevron-${expandedSections.category ? 'up' : 'down'}`}></i>
                        </div>
                    </div>
                    {expandedSections.category && (
                        <div className="filter-section-content">
                            {availableCategories.length > 0 ? (
                                availableCategories.map(cat => (
                                    <div key={cat._id} className="checkbox-item">
                                        <input
                                            type="checkbox"
                                            id={`cat-${cat._id}`}
                                            checked={selectedCategories.includes(cat._id)}
                                            onChange={() => toggleCategory(cat._id)}
                                        />
                                        <label htmlFor={`cat-${cat._id}`}>{cat.name}</label>
                                    </div>
                                ))
                            ) : (
                                <p className="text-muted small">No categories available</p>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FilterSidebar;